/*
 * Hjelpere for PDF-generatoren (uten React/axios, så de kan testes alene).
 */

export type CalendarKind = "year" | "ramadan";

/** Asr-metoden i PDF-en; MOSQUE = moskeens egen innstilling. */
export type AsrChoice = "MOSQUE" | "ONE_X" | "TWO_X" | "MIXED";

export const ASR_OPTIONS: Array<{ value: AsrChoice; label: string }> = [
  { value: "MOSQUE", label: "Moskeens valg" },
  { value: "ONE_X", label: "Standard (1× skygge)" },
  { value: "TWO_X", label: "Hanafi (2× skygge)" },
  { value: "MIXED", label: "Blandet (sommer 1×, vinter 2×)" },
];

/** En Ramadan som kan velges (fra backend, eller beregnet i nettleseren). */
export interface RamadanOption {
  hijriYear: number;
  start: string;
  end: string;
  label: string;
}

/** Årene som kan velges: i fjor til om to år. */
export const calendarYears = (now = new Date()) =>
  Array.from({ length: 4 }, (_, i) => now.getFullYear() - 1 + i);

export const base = (orgNr: string) =>
  `/api/v1/mosques/${encodeURIComponent(orgNr)}/prayer-calendar`;

/** Endepunktet for PDF-en. For Ramadan er `year` Hijri-året (f.eks. 1448). */
export const calendarUrl = (
  orgNr: string,
  kind: CalendarKind,
  year: number,
  asr: AsrChoice = "MOSQUE",
) =>
  `${base(orgNr)}/${kind === "ramadan" ? "ramadan/" : ""}${year}${
    asr === "MOSQUE" ? "" : `?asr=${asr}`
  }`;

export const calendarFilename = (
  kind: CalendarKind,
  year: number,
  mosque: string,
) =>
  `${kind === "ramadan" ? "Ramadan" : "Bønnetider"} ${year}${
    mosque ? ` – ${mosque}` : ""
  }.pdf`;

const SHORT_MONTHS = [
  "jan.",
  "feb.",
  "mar.",
  "apr.",
  "mai",
  "jun.",
  "jul.",
  "aug.",
  "sep.",
  "okt.",
  "nov.",
  "des.",
];

/** «Ramadan 1448 (feb.–mar. 2027)». */
export const ramadanLabel = (hijriYear: number, start: Date, end: Date) => {
  const a = SHORT_MONTHS[start.getMonth()];
  const b = SHORT_MONTHS[end.getMonth()];
  const span =
    start.getFullYear() !== end.getFullYear()
      ? `${a} ${start.getFullYear()} – ${b} ${end.getFullYear()}`
      : start.getMonth() === end.getMonth()
        ? `${a} ${end.getFullYear()}`
        : `${a}–${b} ${end.getFullYear()}`;
  return `Ramadan ${hijriYear} (${span})`;
};

/**
 * Reserve når backend ikke svarer: Ramadan-månedene fra i fjor til om tre år
 * etter Umm al-Qura-kalenderen i nettleseren.
 */
export const localRamadans = (now = new Date()): RamadanOption[] => {
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura-nu-latn", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  } catch {
    return [];
  }
  const found = new Map<number, { start: Date; end: Date }>();
  const day = new Date(now.getFullYear() - 1, 0, 1, 12);
  const stop = new Date(now.getFullYear() + 3, 11, 31, 12);
  while (day <= stop) {
    const parts = fmt.formatToParts(day);
    const num = (t: string) =>
      parseInt(parts.find((p) => p.type === t)?.value ?? "", 10);
    if (num("month") === 9) {
      const y = num("year");
      const d = new Date(day);
      const r = found.get(y);
      if (r) r.end = d;
      else found.set(y, { start: d, end: d });
    }
    day.setDate(day.getDate() + 1);
  }
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  return Array.from(found.entries()).map(([hijriYear, { start, end }]) => ({
    hijriYear,
    start: iso(start),
    end: iso(end),
    label: ramadanLabel(hijriYear, start, end),
  }));
};

/** Første Ramadan som ikke er over ennå (ellers den siste). */
export const defaultRamadan = (
  options: RamadanOption[],
  now = new Date(),
): number | undefined => {
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getDate()).padStart(2, "0")}`;
  return (
    options.find((o) => o.end >= today) ?? options[options.length - 1]
  )?.hijriYear;
};
